/**
 * NCR-2 F1 — THE NCR SCREENS' READ MODEL: what the list, the page and the raise
 * form show and offer, from what the server served.
 *
 * Pure. No React, no I/O, no clock. Every button is one served gate
 * (`gates[act].can_act`, `served.parties.acceptance[].can_decide`,
 * `raise.can_act`); every sentence that says why a person cannot act is the
 * server's own (`gates[act].message`, `served.blockers`), word for word; every
 * "whose turn / by when" is `waiting_on` / `steps`. The rest of the sentences
 * only put served facts into words (companies, dates, versions) and decide
 * nothing — the one exception is the raise form's A5 warning, which the
 * contract asks to show live, composed only from the facts the raise context
 * serves (who is responsible, which positions accept); the door is still the
 * one that refuses.
 *
 * Layout and copy: `docs/design/mockups/prototipo-ncr-v2.html` (approved by
 * the owner, 2026-09-22; D19). Every time on the page carries a label (D19 (10)).
 */

import { fmtShortDay } from './utils';

export const STATE_LABEL = {
  open: 'Open',
  corrective_action_submitted: 'Corrective action submitted',
  corrective_action_approved: 'Corrective action approved',
  close_out_submitted: 'Close-out submitted',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const SEVERITY_LABEL = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
export const SEVERITIES = ['low', 'medium', 'high', 'critical'];

export const POSITION_LABEL = {
  execution: 'Executing Party',
  verification: 'Verification Party',
  acceptance: 'Acceptance Party',
};

export const STEP_TITLES = [
  'Raised',
  'Corrective action proposed',
  'Corrective action approved',
  'Work done, close-out submitted',
  'Closed: close-out approved',
];

// ---------------------------------------------------------------------------
// Dates — DATES-1: the app's one format (`fmtShortDay`), with the year — "Wed
// 24 Sep 2026", an instant "Wed 24 Sep 2026, 08:00" (24 h) on `TIMEZONE`'s
// clock; a calendar date (`YYYY-MM-DD`, the target completion) is a date,
// never shifted by a timezone. `ftime`, an hour alone, is as before.
// ---------------------------------------------------------------------------

export function toDate(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fshort(value) {
  return toDate(value) ? fmtShortDay(value) : '';
}

export function fday(value) {
  return fshort(value);
}

export function ftime(value) {
  const d = toDate(value);
  if (!d) return '';
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h % 12 || 12}:${m} ${h < 12 ? 'am' : 'pm'}`;
}

export function fstamp(value) {
  return toDate(value) ? fmtShortDay(value, { time: true }) : '';
}

/** "A", "A and B", "A, B and C". */
export function names(list) {
  const xs = (list || []).filter(Boolean);
  if (xs.length <= 1) return xs[0] || '';
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

// ---------------------------------------------------------------------------
// The list row, the header chips, and "who is expected to do what, by when"
// ---------------------------------------------------------------------------

export const stateOf = (row) => (row?.flags?.cancelled ? 'cancelled' : row?.state);

/** D19 (8) — the chips of a row / header, in the prototype's order. */
export function chipsFor(row) {
  if (!row) return [];
  const flags = row.flags || {};
  const state = stateOf(row);
  const chips = [{ key: 'state', kind: state === 'closed' ? 'done' : (state === 'cancelled' ? 'void' : 'state'), label: STATE_LABEL[state] || state }];
  const sev = row.severity || 'medium';
  chips.push({ key: 'severity', kind: sev === 'high' || sev === 'critical' ? 'sev-hi' : 'sev', label: SEVERITY_LABEL[sev] || sev });
  if (row.my_turn) chips.push({ key: 'mine', kind: 'mine', label: 'Your turn' });
  if (flags.responsibility_disputed) chips.push({ key: 'disputed', kind: 'dis', label: 'Responsibility disputed' });
  if (flags.target_request_pending) chips.push({ key: 'target', kind: 'flag', label: 'New target date requested' });
  if (flags.response_overdue) chips.push(overdueFlag(row, 'response', 'response', 'Response'));
  if (flags.decision_overdue) chips.push(overdueFlag(row, 'decision', 'decision', 'Decision'));
  if (flags.completion_overdue) chips.push(overdueFlag(row, 'completion', 'target', 'Completion'));
  return chips;
}

// QMP-COPY-1 (decision 30 A) — an overdue flag names its due date only when the
// served `waiting_on` is that same step (`due_kind`, as `ncr_rules.ncr_waiting_on`
// sets it: response → response, decision → decision, completion → target);
// otherwise the label stays without a date — never a date of another step.
function overdueFlag(row, key, dueKind, what) {
  const w = row.waiting_on || {};
  const at = w.due_kind === dueKind && w.due_at && fshort(w.due_at) ? w.due_at : null;
  if (!at) return { key, kind: 'flag', label: `${what} overdue` };
  return { key, kind: 'flag', label: `${what} overdue — Due ${fshort(at)}`, title: `Due ${fstamp(at)}` };
}

const DUE_WORDS = {
  response: ['Response due', 'Response was due'],
  decision: ['Decision due', 'Decision was due'],
  target: ['Target completion', 'Target was'],
};

/**
 * D19 (8) — the turn line: `{dot, lead, who, tail, due, over}`. `who` is the
 * emphasised company part (the prototype's `<b>`), `due` the labelled date.
 */
export function turnLine(row) {
  const w = row?.waiting_on || {};
  const responsible = row?.responsible?.name || '';
  const due = w.due_at && DUE_WORDS[w.due_kind]
    ? `${DUE_WORDS[w.due_kind][w.overdue ? 1 : 0]} ${fshort(w.due_at)}`
    : null;
  switch (w.kind) {
    case 'cancelled':
      return { dot: 'off', lead: `Cancelled on ${fshort(w.at)}`, who: '', tail: '', due: null, over: false };
    case 'closed':
      return { dot: 'ok', lead: `Closed on ${fshort(w.at)}, when the close-out was approved`, who: '', tail: '', due: null, over: false };
    case 'respond':
      return { dot: 'wait', lead: 'Waiting for ', who: names(w.companies) || responsible, tail: ' to give a root cause and propose a corrective action', due, over: !!w.overdue };
    case 'review_corrective_action':
      return { dot: 'wait', lead: 'Waiting for ', who: names(w.companies), tail: ' to review the corrective action', due, over: !!w.overdue };
    case 'carry_out':
      return { dot: 'wait', lead: '', who: names(w.companies) || responsible, tail: ' is carrying out the approved corrective action', due, over: !!w.overdue };
    case 'approve_close_out':
      return { dot: 'wait', lead: 'Waiting for ', who: names(w.companies), tail: ' to approve the close-out', due, over: !!w.overdue };
    default:
      return { dot: 'off', lead: '', who: '', tail: '', due: null, over: false };
  }
}

export const turnText = (row) => {
  const t = turnLine(row);
  return `${t.lead}${t.who}${t.tail}`;
};

/** The list's second line: asset · lot · package · raised by · correcting. */
export function rowSubline(row) {
  const c = row?.context || {};
  const parts = [];
  if (c.asset_label) parts.push(c.asset_label);
  if (c.lot_label) parts.push(`lot ${c.lot_label}`);
  if (c.package_name) parts.push(c.package_name);
  if (row?.raised_by?.name) parts.push(`raised by ${row.raised_by.name}`);
  if (row?.responsible?.name) parts.push(`correcting: ${row.responsible.name}`);
  return parts.join(' · ');
}

/** D19 (8) — All / Open / Waiting for me. */
export function listFilters(items) {
  const all = items || [];
  const open = all.filter((r) => !r.flags?.cancelled && r.state !== 'closed');
  const mine = all.filter((r) => r.my_turn);
  return { all, open, mine };
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export const gate = (page, act) => (page?.gates || {})[act] || { can_act: false, reason: null, message: null };
export const can = (page, act) => gate(page, act).can_act === true;

export const currentCa = (page) => (page?.corrective_actions || []).find((c) => c.is_current) || null;
export const currentCo = (page) => (page?.close_outs || []).find((c) => c.is_current) || null;
export const currentTr = (page) => (page?.target_requests || []).find((c) => c.is_current) || null;
export const lastRejection = (sub) => (sub?.decisions || []).find((d) => d.decision === 'rejected') || null;

/** The lifecycle strip (D19 (1)): each node's look and the note under it. */
export function lifecycle(page) {
  const steps = page?.steps || [];
  const cancelled = !!page?.flags?.cancelled;
  const cur = page?.current_step;
  const closed = page?.state === 'closed';
  const nodes = steps.map((st, i) => {
    let look = 'off';
    if (!cancelled) {
      if (closed || st.status === 'done' || (cur && i + 1 < cur)) look = 'done';
      else if (cur && i + 1 === cur) look = 'cur';
    }
    return { step: i + 1, label: STEP_TITLES[i], who: names(st.companies), look };
  });
  let note = null;
  const ca = currentCa(page);
  const co = currentCo(page);
  if (cancelled) note = 'Cancelled — the lifecycle stopped here.';
  else if (page?.state === 'open' && ca && ca.outcome === 'rejected') {
    note = `Version ${ca.version} was rejected, so step 2 is open again for a new version.`;
  } else if (page?.state === 'corrective_action_approved' && co && co.outcome === 'rejected') {
    note = 'The close-out was rejected, so step 4 is open again. The approved corrective action still stands.';
  }
  return { nodes, note };
}

/**
 * One step card's header status (D19 (1)): `{kind, cls, text, sub}` from the
 * served step. `kind` is the card's look (done / cur / off), `cls` the colour.
 */
export function stepStatus(page, index) {
  const st = (page?.steps || [])[index] || {};
  const n = index + 1;
  const deciding = n === 3 || n === 5;
  const dueSub = st.due_at ? `${st.overdue ? 'was due' : 'due'} ${fshort(st.due_at)}` : null;
  const targetSub = st.due_at ? `${st.overdue ? 'target was' : 'target'} ${fshort(st.due_at)}` : null;
  const sub = n === 4 ? targetSub : dueSub;
  const ca = currentCa(page);
  const co = currentCo(page);
  switch (st.status) {
    case 'your_turn':
      return { kind: 'cur', cls: 'mine', text: deciding ? 'Your decision' : 'Your turn', sub };
    case 'waiting':
      return { kind: 'cur', cls: 'wait', text: `Waiting for ${names(st.waiting_for)}`, sub };
    case 'rejected':
      return { kind: 'off', cls: 'no', text: 'Rejected', sub: `version ${st.version} · ${fshort(st.at)}` };
    case 'stopped':
      return { kind: 'off', cls: '', text: '—', sub: null };
    case 'not_yet':
      return { kind: 'off', cls: '', text: 'Not yet', sub: null };
    case 'done': {
      if (n === 1) return { kind: 'done', cls: 'okc', text: 'Done', sub: `${page?.raised_by_user || ''} · ${fshort(st.at)}` };
      if (n === 2) return { kind: 'done', cls: 'okc', text: 'Done', sub: `Version ${ca?.version || st.version} · ${ca?.by || ''} · ${fshort(ca?.submitted_at || st.at)}` };
      if (n === 3) return { kind: 'done', cls: 'okc', text: 'Approved', sub: fshort(st.at) };
      if (n === 4) return { kind: 'done', cls: 'okc', text: 'Done', sub: `Version ${co?.version || st.version} · ${co?.by || ''} · ${fshort(co?.submitted_at || st.at)}` };
      return { kind: 'done', cls: 'okc', text: 'Closed', sub: fshort(st.at) };
    }
    default:
      return { kind: 'off', cls: '', text: 'Not yet', sub: null };
  }
}

/** The served sentence that says why THIS person cannot decide (D19 (9)). */
export const blockerMessage = (page) => ((page?.served?.blockers || [])[0] || {}).message || null;

/** The acts of the "⋯" menu (D19 (7)), each only when its gate allows. The
 *  acts the prototype also shows in place (dispute / withdraw and make safe in
 *  step 2) stay there too; the menu is a second door, never the only one
 *  (NCR-FE-2 B3). The red act closes the list, after a separator. */
export const MENU_ACTS = [
  { act: 'reassign', label: 'Reassign the responsible company', hint: 'The new company starts its own corrective action.' },
  { act: 'change_acceptance_parties', label: 'Change who must accept', hint: 'Decisions already given on the current stage stop counting.' },
  { act: 'amend', label: 'Amend the description', hint: 'The original stays in the record.' },
  { act: 'maintain_assignment', label: 'Keep the current assignment', hint: 'The dispute stays on record.' },
  { act: 'dispute', label: 'Dispute responsibility', hint: 'Does not pause anything; not an admission.' },
  { act: 'withdraw_dispute', label: 'Withdraw the dispute', hint: null },
  { act: 'record_make_safe', label: 'Record immediate action (make safe)', hint: 'This is not the corrective action.' },
  { act: 'cancel', label: 'Cancel this NCR', hint: 'Nothing is deleted.', danger: true },
];

export const menuItems = (page) => MENU_ACTS.filter((m) => can(page, m.act));

// ---------------------------------------------------------------------------
// History (D19 (6)) — every formal fact as the prototype words it
// ---------------------------------------------------------------------------

const quoted = (reason) => (reason ? ` — “${reason}”` : '');

export function historyLine(e) {
  const who = e.kind === 'closed' || e.kind === 'target_now'
    ? 'ConstructQA'
    : [e.by, e.company].filter(Boolean).join(', ');
  const verdict = (d) => (d === 'approved' ? 'Approved' : 'Rejected');
  let what;
  switch (e.kind) {
    case 'raised': what = `Raised this NCR against ${e.responsible}`; break;
    case 'amended': what = `Amended the description to Rev ${e.version}${quoted(e.reason)}`; break;
    case 'reassigned':
      what = `Reassigned responsibility from ${e.from} to ${e.to}${e.no_longer_accepts ? ` (${e.to} no longer accepts)` : ''}${quoted(e.reason)}`;
      break;
    case 'acceptors_changed': what = `Changed who must accept to ${names(e.to)}${quoted(e.reason)}`; break;
    case 'corrective_action_submitted': what = `Submitted corrective action, version ${e.version}`; break;
    case 'corrective_action_decided': what = `${verdict(e.decision)} corrective action version ${e.version}${quoted(e.reason)}`; break;
    case 'close_out_submitted': what = `Submitted close-out, version ${e.version}`; break;
    case 'close_out_decided': what = `${verdict(e.decision)} close-out version ${e.version}${quoted(e.reason)}`; break;
    case 'target_submitted': what = `Asked for a new target date, ${fshort(e.date)}${quoted(e.reason)}`; break;
    case 'target_decided': what = `${verdict(e.decision)} the new target date ${fshort(e.date)}${quoted(e.reason)}`; break;
    case 'target_now': what = `Target completion is now ${fshort(e.date)}`; break;
    case 'dispute_raised': what = `Disputed responsibility${quoted(e.reason)}`; break;
    case 'dispute_withdrawn': what = 'Withdrew the dispute'; break;
    case 'dispute_superseded': what = 'The dispute was superseded'; break;
    case 'dispute_assignment_maintained': what = `Kept the current assignment${quoted(e.reason)}`; break;
    case 'make_safe': what = 'Recorded immediate action'; break;
    case 'closed': what = 'NCR closed — every accepting company approved the close-out'; break;
    case 'cancelled': what = `Cancelled this NCR${quoted(e.reason)}`; break;
    default: what = e.kind;
  }
  return { what, who, at: fstamp(e.at) };
}

// ---------------------------------------------------------------------------
// The raise form (F4) — A5 shown live from the served facts only
// ---------------------------------------------------------------------------

/**
 * `{bad, none, others}` for the selected acceptance positions and the
 * responsible company, over the raise context's package positions. The door
 * refuses the same configuration (NCR_ACCEPTANCE_SELF_ONLY / _EMPTY) whatever
 * this says.
 */
export function acceptanceWarning(context, responsibleId, selected) {
  const positions = context?.package?.positions || [];
  if (!context?.package?.declared) return { bad: false, none: false, others: [] };
  const chosen = positions.filter((p) => (selected || []).includes(p.position));
  const others = positions.filter((p) => p.participation_id !== responsibleId);
  const onlyResponsible = chosen.length > 0 && chosen.every((p) => p.participation_id === responsibleId);
  const otherNames = [...new Set(others.map((p) => p.name))];
  return { bad: onlyResponsible && others.length > 0, none: chosen.length === 0, others: otherNames };
}
